import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Cpu, Sparkles, Loader2, ArrowRight } from "lucide-react";

const TEMPLATES = [
  { v: "qa_pairs", label: "Q&A Pairs (fine-tuning)" },
  { v: "summarize", label: "Summarization" },
  { v: "instruction", label: "Instruction Tuning" },
  { v: "keywords", label: "Keyword Extraction" },
];

export default function Synthesizer({ activeCrawl }) {
  const [template, setTemplate] = useState("qa_pairs");
  const [limit, setLimit] = useState(5);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);

  const run = async () => {
    if (!activeCrawl) { toast.error("Select a crawl job first"); return; }
    setRunning(true);
    setResults([]);
    try {
      const data = await api.synthesize(activeCrawl.id, { template, limit: Number(limit) });
      setResults(data.results);
      toast.success(`Synthesized ${data.count} sample(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Synthesis failed — check LLM settings & connection");
    } finally { setRunning(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <header>
        <h2 className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">Synthesizer</h2>
        <p className="text-sm text-slate-500 mt-1">
          Transform scraped pages into training samples using your Ollama / OpenAI-compatible LLM.
        </p>
      </header>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-5 space-y-5">
        <div className="flex items-center gap-2">
          <Cpu className="h-4 w-4 text-purple-400" />
          <h3 className="font-display font-bold text-base">Batch Transformation</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Template</Label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger data-testid="synth-template-select" className="bg-[#0E1420] border-border text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATES.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Pages to process</Label>
            <Input data-testid="synth-limit-input" type="number" min={1} max={50} value={limit}
              onChange={(e) => setLimit(e.target.value)} className="bg-[#0E1420] border-border font-mono text-sm" />
          </div>
          <Button data-testid="synth-run-button" onClick={run} disabled={running}
            className="bg-purple-500 hover:bg-purple-400 text-white font-semibold">
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            {running ? "Synthesizing…" : "Run Synthesis"}
          </Button>
        </div>
        <p className="text-[11px] font-mono text-slate-600">
          Note: the LLM host must be reachable from this server. For a local Ollama, expose it publicly or run this app locally.
        </p>
      </div>

      <div className="space-y-4" data-testid="synth-results">
        {results.map((r, i) => (
          <div key={i} className="rounded-lg border border-border bg-[#0B0F18] overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border bg-[#0E1420] flex items-center gap-2">
              <span className="font-mono text-[11px] text-slate-500">#{i + 1}</span>
              <span className="text-sm text-slate-300 truncate">{r.title}</span>
            </div>
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
              <div className="p-4">
                <p className="text-[10px] font-mono uppercase text-slate-600 mb-2">Source</p>
                <p className="text-xs text-slate-400 line-clamp-6 whitespace-pre-wrap">{r.input_preview}</p>
              </div>
              <div className="p-4 bg-[#04060A]/40">
                <p className="text-[10px] font-mono uppercase text-purple-400 mb-2 flex items-center gap-1">
                  <ArrowRight className="h-3 w-3" /> Training Sample
                </p>
                {r.error ? (
                  <p className="text-xs text-rose-400 font-mono">{r.error}</p>
                ) : (
                  <pre className="terminal-scroll text-xs text-emerald-300 whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">{r.output}</pre>
                )}
              </div>
            </div>
          </div>
        ))}
        {results.length === 0 && !running && (
          <div className="rounded-lg border border-dashed border-border p-10 text-center text-slate-600 font-mono text-sm">
            No samples yet. Configure the LLM in Settings, then run synthesis.
          </div>
        )}
      </div>
    </div>
  );
}
